/* Admin Subscription Plans editor (iter-21).
 *
 * Edits the `app_config.subscription_plans` catalog so admins can adjust
 * tier names, prices, monthly/yearly token grants, Copyscape allowances,
 * feature bullets, and the "best" highlight — all without touching Mongo.
 *
 * Persisted via PUT /api/admin/subscriptions/plans which validates:
 *   - unique plan ids (kebab-case)
 *   - at most one "best" plan
 *   - prices in [0, 1_000_000]
 *   - tokens in [0, 10_000_000_000]
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, Plus, Trash2, Crown, RotateCcw, Loader2, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader, Field } from './_shared';
import { cn } from '@/lib/utils';
import { openConfirm } from '@/components/admin/ConfirmDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// iter55g29 — Compact stat cell used in the trial cohort banner.
const Stat = ({ label, value }) => (
  <div>
    <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-mono-stat text-[13px] font-semibold">{value}</div>
  </div>
);

const emptyPlan = (id = '') => ({
  // `_uid` is a stable client-side row identity used purely as the React
  // `key` for the editor cards. We can't use `p.id` for the key because
  // the ID field is itself user-editable — keying by it would unmount/
  // remount the card on every keystroke. The `_uid` is stripped before
  // `PUT /api/admin/subscriptions/plans`.
  _uid: (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `uid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
  id, name: '', tagline: '',
  monthly: { price_usd: 0, tokens: 0 },
  yearly:  { price_usd: 0, tokens: 0 },
  copyscape_included: 0,
  features: [],
  best: false,
  markup_pct: null,   // iter55z-m — null = inherit global markup
  // iter55g29 — capability + numeric-cap matrix + promo lifecycle.
  capabilities: {},
  numeric_caps: {},
  is_trial: false,
  is_free: false,
  is_promo: false,
  allowed_model_ids: null,
  trial_duration_days: null,
  expires_at: null,
  visible_to_public: true,
  byok: false,
  byok_required_providers: [],
});

const withRowUid = (p) => ({
  ...p,
  _uid: p._uid || (
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `uid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  ),
});

export default function SubscriptionPlansPanel() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // iter55z-m — Global + token-pack markup knobs (persisted on app_config).
  const [globalMarkup, setGlobalMarkup] = useState(0);
  const [tokenMarkup,  setTokenMarkup]  = useState('');   // '' means "inherit global"
  const [markupBusy, setMarkupBusy] = useState(false);
  // iter55g29 — Capability + numeric-cap registry (from backend) and
  // trial cohort analytics (rendered as a compact banner card above
  // the plan editor cards).
  const [registry,   setRegistry]   = useState({ capabilities: [], numeric_caps: [], models: [] });
  const [trialStats, setTrialStats] = useState(null);
  const [expanded,   setExpanded]   = useState({});   // {planUid: bool} — capability matrix expand toggle

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminApi.listPlans();
      setPlans((r.plans || []).map(withRowUid));
      // Also load the markup knobs from app_config.
      try {
        const cfg = await axios.get(`${API}/admin/config`, { withCredentials: true });
        setGlobalMarkup(Number(cfg.data?.pricing_markup_pct ?? 0));
        const tp = cfg.data?.token_pack_markup_pct;
        setTokenMarkup(tp === null || tp === undefined ? '' : String(tp));
      } catch { /* leave defaults */ }
      // iter55g29 — Capability registry (soft-fail on any 4xx).
      try {
        const reg = await axios.get(`${API}/admin/subscriptions/capabilities/registry`,
                                    { withCredentials: true });
        setRegistry(reg.data || { capabilities: [], numeric_caps: [] });
      } catch { /* registry missing → empty matrix, cards still work */ }
      try {
        const t = await axios.get(`${API}/admin/subscriptions/trials/analytics`,
                                  { withCredentials: true });
        setTrialStats(t.data);
      } catch { /* trial analytics optional */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load plans.');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const update = (idx, patch) => setPlans(arr => arr.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const updateCadence = (idx, cad, patch) => setPlans(arr =>
    arr.map((p, i) => i === idx ? { ...p, [cad]: { ...p[cad], ...patch } } : p));

  const setBest = (idx) => setPlans(arr => arr.map((p, i) => ({ ...p, best: i === idx })));
  const removePlan = (idx) => setPlans(arr => arr.filter((_, i) => i !== idx));
  const addPlan = () => setPlans(arr => [...arr, emptyPlan(`plan_${arr.length + 1}`)]);

  const save = async () => {
    setBusy(true);
    try {
      // Normalise: numeric coercion + features split on newline.
      // Strip client-only `_uid` row identity before persisting.
      const payload = plans.map((p) => {
        // Strip client-only `_uid` row identity before persisting.
        const { _uid: _drop, ...rest } = p;
        return {
          ...rest,
          monthly: { price_usd: Number(p.monthly.price_usd || 0), tokens: Number(p.monthly.tokens || 0) },
          yearly:  { price_usd: Number(p.yearly.price_usd || 0),  tokens: Number(p.yearly.tokens || 0) },
          copyscape_included: Number(p.copyscape_included || 0),
          features: Array.isArray(p.features) ? p.features : String(p.features || '').split('\n').filter(Boolean),
          // iter55z-m — allow "" / null to clear the override.
          markup_pct: (p.markup_pct === '' || p.markup_pct === null || p.markup_pct === undefined)
            ? null
            : Math.max(0, Math.min(100, Number(p.markup_pct))),
        };
      });
      await adminApi.savePlans(payload);
      toast.success('Subscription plans saved.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const resetDefaults = async () => {
    if (!(await openConfirm({
      title: 'Reset to built-in defaults?',
      description: 'This will replace the current plans with Starter / Pro / Power.',
      tone: 'destructive',
    }))) return;
    setBusy(true);
    try {
      await adminApi.resetPlans();
      toast.success('Defaults restored.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed.');
    } finally { setBusy(false); }
  };

  // iter55z-m — Persist the global + AI-token markup knobs.
  const saveMarkups = async () => {
    setMarkupBusy(true);
    try {
      const patch = {
        pricing_markup_pct: Math.max(0, Math.min(100, Number(globalMarkup) || 0)),
      };
      // tokenMarkup='' → don't include, backend keeps existing / falls back
      // to the global slider. Numeric value → override.
      if (tokenMarkup !== '' && tokenMarkup !== null && tokenMarkup !== undefined) {
        patch.token_pack_markup_pct = Math.max(0, Math.min(100, Number(tokenMarkup)));
      }
      await axios.put(`${API}/admin/config`, patch, { withCredentials: true });
      toast.success('Markup saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save markup.');
    } finally { setMarkupBusy(false); }
  };

  if (loading) {
    return (
      <section>
        <PanelHeader title="Subscription Plans" />
        <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading plans…
        </div>
      </section>
    );
  }

  return (
    <section data-testid="admin-plans-panel">
      <PanelHeader title="Subscription Plans" />
      <p className="mb-4 text-[11.5px] text-muted-foreground">
        These plans appear on the Subscribe tab of the Paywall dialog. Prices here are <b>base</b>;
        markup is applied automatically at read-time. Mark one plan as &quot;Best&quot; to highlight it.
      </p>

      {/* iter55z-m — Global + AI-token markup controls. */}
      <div
        data-testid="admin-plans-markup-card"
        className="mb-4 rounded-md border border-primary/30 bg-primary/5 p-3"
      >
        <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold">
          <Percent className="h-3.5 w-3.5 text-primary" /> Markup controls
        </div>
        <p className="mb-3 text-[11px] text-muted-foreground">
          <b>Global markup</b> applies to every subscription plan by default (base × 1 + N%).
          Each plan can override it below. <b>AI-token pack markup</b> is a separate slider
          for one-off token top-ups — leave blank to inherit the global markup.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="Global markup (%)">
            <Input
              type="number"
              min={0}
              max={100}
              value={globalMarkup}
              onChange={(e) => setGlobalMarkup(e.target.value)}
              className="h-8 font-mono-stat text-[12px]"
              data-testid="plans-global-markup"
            />
          </Field>
          <Field label="AI token pack markup (%)" hint="Leave blank to reuse global">
            <Input
              type="number"
              min={0}
              max={100}
              placeholder={`inherit (${globalMarkup}%)`}
              value={tokenMarkup}
              onChange={(e) => setTokenMarkup(e.target.value)}
              className="h-8 font-mono-stat text-[12px]"
              data-testid="plans-token-markup"
            />
          </Field>
          <div className="flex items-end">
            <Button
              size="sm"
              onClick={saveMarkups}
              disabled={markupBusy}
              className="h-8 w-full gap-1.5 text-[11.5px]"
              data-testid="plans-save-markup"
            >
              {markupBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save markup
            </Button>
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Button size="sm" onClick={save} disabled={busy} className="gap-1.5" data-testid="plans-save">
          <Save className="h-3.5 w-3.5" /> Save plans
        </Button>
        <Button size="sm" variant="outline" onClick={addPlan} className="gap-1.5" data-testid="plans-add">
          <Plus className="h-3.5 w-3.5" /> Add plan
        </Button>
        <Button size="sm" variant="outline" onClick={resetDefaults} disabled={busy} className="gap-1.5" data-testid="plans-reset">
          <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
        </Button>
      </div>

      {/* iter55g29 — Trial cohort analytics banner. Renders only when
       *  we have trial signups on record. */}
      {trialStats && trialStats.total_trials > 0 && (
        <div
          className="mb-4 rounded-md border border-blue-500/40 bg-blue-500/5 p-3"
          data-testid="admin-plans-trial-stats"
        >
          <div className="mb-2 text-[12px] font-semibold text-blue-700 dark:text-blue-300">
            Trial cohort — {trialStats.total_trials} signups
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5 text-[11px]">
            <Stat label="Active"    value={trialStats.active_trials} />
            <Stat label="Converted" value={trialStats.converted} />
            <Stat label="Expired · no convert" value={trialStats.expired_unconverted} />
            <Stat label="Conversion" value={`${(trialStats.conversion_rate * 100).toFixed(1)}%`} />
            <Stat label="Avg LLM cost / free user"
                  value={`$${(trialStats.avg_cost_per_free_user_cents / 100).toFixed(2)}`} />
          </div>
          <div className="mt-2 text-[10.5px] text-muted-foreground">
            Total LLM spend on trials: <b>${(trialStats.total_llm_spend_on_trials_cents / 100).toFixed(2)}</b>
            {trialStats.converted > 0 && (
              <> · avg cost/converted user: <b>${(trialStats.avg_cost_per_converted_user_cents / 100).toFixed(2)}</b></>
            )}
          </div>
          {/* iter55g32 — Cost-velocity alert. Flags any active trial
           *  user whose burn rate will blow the ceiling in the next 24h,
           *  so ops can proactively convert or gift a discount. */}
          {(trialStats.at_risk_burn_rate || []).length > 0 && (
            <div className="mt-3 rounded-sm border border-amber-500/50 bg-amber-500/10 p-2"
                 data-testid="admin-plans-at-risk-list">
              <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                {trialStats.at_risk_burn_rate.length} trial user(s) on pace to hit the ${(trialStats.ceiling_cents / 100).toFixed(0)} ceiling in the next 24h
              </div>
              <ul className="space-y-0.5 text-[11px]">
                {trialStats.at_risk_burn_rate.slice(0, 8).map((r) => (
                  <li key={r.user_id} className="flex items-center justify-between font-mono-stat">
                    <span className="truncate">{r.user_id}</span>
                    <span>
                      spent <b>${(r.spent_cents / 100).toFixed(2)}</b> · projected 24h <b>${(r.projected_24h_cents / 100).toFixed(2)}</b>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {plans.map((p, idx) => (
          <div key={p._uid || idx}
               className={cn('rounded-md border bg-card p-4',
                 p.best ? 'border-primary/70 ring-1 ring-primary/30' : 'border-border')}
               data-testid={`plans-card-${p.id || idx}`}>
            <div className="mb-3 flex items-center gap-2">
              <Crown className={cn('h-3.5 w-3.5', p.best ? 'text-primary' : 'text-muted-foreground')} />
              <span className="font-mono-stat text-[12px] font-semibold">{p.id || '(new plan)'}</span>
              {p.is_trial && (
                <span className="rounded-sm bg-blue-500/15 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-blue-700 dark:text-blue-300">
                  trial
                </span>
              )}
              {p.is_free && (
                <span className="rounded-sm bg-emerald-500/15 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                  free
                </span>
              )}
              {p.is_promo && (
                <span className="rounded-sm bg-orange-500/15 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-orange-700 dark:text-orange-300">
                  promo
                </span>
              )}
              <button onClick={() => setBest(idx)}
                       data-testid={`plans-best-${idx}`}
                       className={cn('ml-auto rounded-sm px-1.5 py-0.5 text-[10px]',
                         p.best ? 'bg-primary/15 text-primary' : 'bg-secondary text-muted-foreground hover:bg-muted')}>
                {p.best ? 'Best plan' : 'Mark best'}
              </button>
              <button onClick={() => removePlan(idx)}
                       data-testid={`plans-remove-${idx}`}
                       className="text-destructive hover:text-destructive/70">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Field label="ID (kebab/underscore)">
                <Input value={p.id} onChange={(e) => update(idx, { id: e.target.value.replace(/[^a-z0-9_]/g, '').slice(0, 32) })}
                        className="h-8 font-mono-stat text-[12px]"
                        data-testid={`plans-id-${idx}`} />
              </Field>
              <Field label="Display name">
                <Input value={p.name} onChange={(e) => update(idx, { name: e.target.value })}
                        className="h-8 text-[12px]" data-testid={`plans-name-${idx}`} />
              </Field>
              <Field label="Tagline" className="col-span-2">
                <Input value={p.tagline || ''} onChange={(e) => update(idx, { tagline: e.target.value })}
                        className="h-8 text-[12px]" data-testid={`plans-tagline-${idx}`} />
              </Field>
              <Field label="Monthly price (USD)">
                <Input type="number" min={0} step="0.01" value={p.monthly.price_usd}
                        onChange={(e) => updateCadence(idx, 'monthly', { price_usd: e.target.value })}
                        className="h-8 font-mono-stat text-[12px]" data-testid={`plans-monthly-price-${idx}`} />
              </Field>
              <Field label="Monthly tokens">
                <Input type="number" min={0} step="100000" value={p.monthly.tokens}
                        onChange={(e) => updateCadence(idx, 'monthly', { tokens: e.target.value })}
                        className="h-8 font-mono-stat text-[12px]" data-testid={`plans-monthly-tokens-${idx}`} />
              </Field>
              <Field label="Yearly price (USD)">
                <Input type="number" min={0} step="0.01" value={p.yearly.price_usd}
                        onChange={(e) => updateCadence(idx, 'yearly', { price_usd: e.target.value })}
                        className="h-8 font-mono-stat text-[12px]" data-testid={`plans-yearly-price-${idx}`} />
              </Field>
              <Field label="Yearly tokens">
                <Input type="number" min={0} step="100000" value={p.yearly.tokens}
                        onChange={(e) => updateCadence(idx, 'yearly', { tokens: e.target.value })}
                        className="h-8 font-mono-stat text-[12px]" data-testid={`plans-yearly-tokens-${idx}`} />
              </Field>
              <Field label="Copyscape scans / period">
                <Input type="number" min={0} value={p.copyscape_included}
                        onChange={(e) => update(idx, { copyscape_included: e.target.value })}
                        className="h-8 font-mono-stat text-[12px]" data-testid={`plans-cs-${idx}`} />
              </Field>
              <Field label="Plan markup override (%)" hint="Blank = use global markup">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  placeholder={`inherit (${globalMarkup}%)`}
                  value={p.markup_pct === null || p.markup_pct === undefined ? '' : p.markup_pct}
                  onChange={(e) => update(idx, { markup_pct: e.target.value === '' ? null : e.target.value })}
                  className="h-8 font-mono-stat text-[12px]"
                  data-testid={`plans-markup-${idx}`}
                />
              </Field>
              <Field label="Features (one per line)" className="col-span-2">
                <textarea
                  value={(Array.isArray(p.features) ? p.features : []).join('\n')}
                  onChange={(e) => update(idx, { features: e.target.value.split('\n') })}
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-[11.5px]"
                  data-testid={`plans-features-${idx}`}
                />
              </Field>
            </div>

            {/* iter55g29 — Promo / trial lifecycle metadata. */}
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border/50 pt-3">
              <label className="col-span-2 flex flex-wrap items-center gap-3 text-[11px]">
                <span className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={!!p.is_free}
                         onChange={(e) => update(idx, { is_free: e.target.checked })}
                         data-testid={`plans-is-free-${idx}`} />
                  Permanent free tier
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={!!p.is_trial}
                         onChange={(e) => update(idx, { is_trial: e.target.checked })}
                         data-testid={`plans-is-trial-${idx}`} />
                  Free-trial plan
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={!!p.is_promo}
                         onChange={(e) => update(idx, { is_promo: e.target.checked })}
                         data-testid={`plans-is-promo-${idx}`} />
                  Ephemeral promo (advertising)
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={p.visible_to_public !== false}
                         onChange={(e) => update(idx, { visible_to_public: e.target.checked })}
                         data-testid={`plans-visible-public-${idx}`} />
                  Visible on public pricing page
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={!!p.byok}
                         onChange={(e) => update(idx, { byok: e.target.checked })}
                         data-testid={`plans-byok-${idx}`} />
                  BYOK (bring your own key) opt-in
                </span>
              </label>
              {p.is_trial && (
                <Field label="Trial duration (days)">
                  <Input type="number" min={1} max={365} value={p.trial_duration_days ?? ''}
                         onChange={(e) => update(idx, {
                           trial_duration_days: e.target.value === '' ? null : Number(e.target.value),
                         })}
                         className="h-8 font-mono-stat text-[12px]"
                         data-testid={`plans-trial-days-${idx}`} />
                </Field>
              )}
              {p.is_promo && (
                <Field label="Expires at (ISO date)" hint="Promos auto-hide from the pricing page after this date">
                  <Input type="text" placeholder="2026-03-31" value={p.expires_at || ''}
                         onChange={(e) => update(idx, { expires_at: e.target.value || null })}
                         className="h-8 font-mono-stat text-[12px]"
                         data-testid={`plans-expires-${idx}`} />
                </Field>
              )}
            </div>

            {/* iter55g29 — Capability matrix. Collapsed by default; the
             *  registry drives the row list so new features show up
             *  automatically. */}
            {registry.capabilities.length > 0 && (
              <div className="mt-3 border-t border-border/50 pt-3">
                <button
                  type="button"
                  onClick={() => setExpanded((prev) => ({ ...prev, [p._uid]: !prev[p._uid] }))}
                  data-testid={`plans-caps-toggle-${idx}`}
                  className="flex w-full items-center justify-between text-[11.5px] font-semibold hover:text-primary"
                >
                  <span>Capability matrix ({Object.values(p.capabilities || {}).filter(Boolean).length}/{registry.capabilities.length} enabled)</span>
                  <span className="text-[10px] text-muted-foreground">{expanded[p._uid] ? 'hide' : 'edit'}</span>
                </button>
                {expanded[p._uid] && (
                  <>
                    <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {Object.entries(registry.capabilities.reduce((acc, c) => {
                        acc[c.category] = acc[c.category] || [];
                        acc[c.category].push(c);
                        return acc;
                      }, {})).map(([cat, items]) => (
                        <div key={cat} className="rounded-sm bg-secondary/40 p-1.5">
                          <div className="mb-1 text-[9.5px] uppercase tracking-wider text-muted-foreground">{cat}</div>
                          <div className="space-y-0.5">
                            {items.map((c) => (
                              <label key={c.key} className="flex items-center gap-1.5 text-[11px]"
                                     title={c.key}>
                                <input
                                  type="checkbox"
                                  checked={!!(p.capabilities || {})[c.key]}
                                  onChange={(e) => update(idx, {
                                    capabilities: { ...(p.capabilities || {}), [c.key]: e.target.checked },
                                  })}
                                  data-testid={`plans-cap-${idx}-${c.key}`}
                                />
                                {c.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {registry.numeric_caps.length > 0 && (
                      <div className="mt-2 rounded-sm border border-primary/30 bg-primary/5 p-2">
                        <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-primary">
                          Numeric caps (trial / promo throttles — leave blank for unlimited)
                        </div>
                        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                          {registry.numeric_caps.map((nc) => (
                            <label key={nc.key} className="flex items-center gap-2 text-[11px]">
                              <span className="flex-1">{nc.label}</span>
                              <Input
                                type="number"
                                min={0}
                                value={(p.numeric_caps || {})[nc.key] ?? ''}
                                onChange={(e) => update(idx, {
                                  numeric_caps: {
                                    ...(p.numeric_caps || {}),
                                    [nc.key]: e.target.value === '' ? undefined : Number(e.target.value),
                                  },
                                })}
                                className="h-6 w-20 font-mono-stat text-[11px]"
                                placeholder="unlimited"
                                data-testid={`plans-cap-num-${idx}-${nc.key}`}
                              />
                              <span className="text-[9.5px] text-muted-foreground">{nc.unit}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {registry.models?.length > 0 && (
                      <div className="mt-2 rounded-sm border border-border bg-background p-2" data-testid={`plans-model-pool-${idx}`}>
                        <div className="mb-1 text-[10.5px] font-semibold uppercase text-foreground">Allowed AI pool models</div>
                        <p className="mb-2 text-[10px] text-muted-foreground">These choices are enforced by the API as well as hidden from this tier’s model picker.</p>
                        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                          {registry.models.map((model) => {
                            const allIds = registry.models.map((item) => item.id);
                            const selected = p.allowed_model_ids == null ? allIds : p.allowed_model_ids;
                            const checked = selected.includes(model.id);
                            return (
                              <label key={model.id} className="flex items-center gap-1.5 text-[11px]">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(event) => update(idx, {
                                    allowed_model_ids: event.target.checked
                                      ? [...new Set([...selected, model.id])]
                                      : selected.filter((id) => id !== model.id),
                                  })}
                                  data-testid={`plans-model-${idx}-${model.id}`}
                                />
                                <span>{model.label}</span>
                                <span className="text-[9px] uppercase text-muted-foreground">{model.provider}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
